import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Bookmark, Calendar, MapPin, Plus, Share2 } from "lucide-react";
import { toast } from "sonner";

import AdventureCard from "@/components/adventures/AdventureCard";
import AdventureHero from "@/components/adventures/AdventureHero";
import KindredShareSheet from "@/components/KindredShareSheet";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ADVENTURE_TYPES, extractEventIdFromAdventureDescription, formatDateRange } from "@/lib/adventure-helpers";
import { buildKindredWebUrl } from "@/lib/kindredShare";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppInlineAlert,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppSegmentedControl,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";
import {
  filterEventsByDistance,
  filterEventsForView,
  orderAdventuresByNextOccurrence,
  orderEventsForMember,
  WHATS_ON_DISTANCE_OPTIONS,
  type WhatsOnDistanceMiles,
} from "../../shared/activities";
import { lookupMemberCoordinates } from "../../shared/location";

type ActivitySection = "all" | "by-type" | "whats-on";
type EventFilter = "all" | "free" | "low-cost" | "this-week" | "school-holidays";

const SCHOOL_HOLIDAYS: [Date, Date][] = [
  [new Date("2026-04-02"), new Date("2026-04-17")],
  [new Date("2026-05-25"), new Date("2026-05-29")],
  [new Date("2026-07-22"), new Date("2026-09-02")],
  [new Date("2026-10-26"), new Date("2026-10-30")],
  [new Date("2026-12-19"), new Date("2027-01-02")],
];

const parseSection = (value: string | null): ActivitySection => {
  if (value === "by-type" || value === "whats-on") return value;
  return "all";
};

const waitForSettledSession = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const shouldRetryPublishedEventsResult = (
  result: { data: unknown[] | null; error: unknown },
  userId?: string | null,
) => Boolean(userId) && (Boolean(result.error) || (result.data?.length ?? 0) === 0);

const fetchPublishedEventsForMember = async (userId?: string | null) => {
  const runQuery = () => supabase.from("events").select("*").eq("is_published", true).order("event_date", { ascending: true });

  let result = await runQuery();

  if (!shouldRetryPublishedEventsResult(result, userId)) {
    return result;
  }

  for (const delay of [120, 320, 750, 1500]) {
    await waitForSettledSession(delay);
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (session?.user?.id !== userId) {
      continue;
    }

    const retry = await runQuery();
    result = retry;

    if (!shouldRetryPublishedEventsResult(retry, userId)) {
      break;
    }
  }

  return result;
};

const Activities = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [adventures, setAdventures] = useState<any[]>([]);
  const [memberCounts, setMemberCounts] = useState<Record<string, { interested: number; going: number }>>({});
  const [events, setEvents] = useState<any[]>([]);
  const [linkedAdventureByEvent, setLinkedAdventureByEvent] = useState<Record<string, { id: string; going: number; interested: number }>>({});
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [postcode, setPostcode] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [filter, setFilter] = useState<EventFilter>("all");
  const [distanceLimitMiles, setDistanceLimitMiles] = useState<WhatsOnDistanceMiles>(null);
  const [loading, setLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState<string | null>(null);
  const [whatsOnError, setWhatsOnError] = useState<string | null>(null);

  const section = parseSection(searchParams.get("section"));

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setActivitiesError(null);
      setWhatsOnError(null);

      const [adventuresResult, eventsResult, profileResult, savedEventsResult, linkedRowsResult] = await Promise.all([
        supabase
          .from("adventures")
          .select("*, profiles!adventures_user_id_fkey(display_name)")
          .eq("status", "active")
          .order("date_start", { ascending: true }),
        fetchPublishedEventsForMember(user?.id),
        user
          ? supabase.from("my_private_profile").select("postcode, country_code").maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        user ? supabase.from("saved_events").select("event_id").eq("user_id", user.id) : Promise.resolve({ data: null, error: null }),
        supabase.from("adventures").select("id, title, description").eq("status", "active"),
      ]);

      const nextAdventures = adventuresResult.data ?? [];
      const eventRows = eventsResult.data ?? [];
      const profile = profileResult.data ?? null;
      const savedEvents = savedEventsResult.data ?? [];
      const linkedRows = linkedRowsResult.data ?? [];
      const userCoordinates = await lookupMemberCoordinates(profile?.country_code ?? null, profile?.postcode ?? null);

      if (adventuresResult.error) {
        console.error("Could not load Activities:", adventuresResult.error);
        setActivitiesError("Activities are taking a moment to refresh. Reopen this tab in a moment and they should catch up.");
      }

      if (eventsResult.error) {
        console.error("Could not load What’s On events:", eventsResult.error);
        setWhatsOnError("What’s On is taking a moment to refresh. Reopen this tab in a moment and it should catch up.");
      }

      if (profileResult.error) {
        console.error("Could not load member postcode for What’s On:", profileResult.error);
      }

      if (savedEventsResult.error) {
        console.error("Could not load saved events for What’s On:", savedEventsResult.error);
      }

      if (linkedRowsResult.error) {
        console.error("Could not load linked adventures for What’s On:", linkedRowsResult.error);
      }

      const adventureIds = nextAdventures?.map((adventure: any) => adventure.id) ?? [];
      const { data: members } = adventureIds.length
        ? await supabase.from("adventure_members").select("adventure_id, status").in("adventure_id", adventureIds)
        : { data: [] as any[] };

      const nextMemberCounts: Record<string, { interested: number; going: number }> = {};
      for (const member of members ?? []) {
        if (!nextMemberCounts[member.adventure_id]) {
          nextMemberCounts[member.adventure_id] = { interested: 0, going: 0 };
        }
        if (member.status === "interested") nextMemberCounts[member.adventure_id].interested += 1;
        if (member.status === "joined") nextMemberCounts[member.adventure_id].going += 1;
      }

      const linkedAdventures = (linkedRows ?? []).filter((adventure: any) => extractEventIdFromAdventureDescription(adventure.description));
      const linkedAdventureIds = linkedAdventures.map((adventure: any) => adventure.id);
      const { data: linkedMembers } = linkedAdventureIds.length
        ? await supabase.from("adventure_members").select("adventure_id, status").in("adventure_id", linkedAdventureIds)
        : { data: [] as any[] };

      const nextLinkedAdventureByEvent: Record<string, { id: string; going: number; interested: number }> = {};
      linkedAdventures.forEach((adventure: any) => {
        const eventId = extractEventIdFromAdventureDescription(adventure.description);
        if (!eventId) return;

        const matchingMembers = (linkedMembers ?? []).filter((member: any) => member.adventure_id === adventure.id);
        nextLinkedAdventureByEvent[eventId] = {
          id: adventure.id,
          interested: matchingMembers.filter((member: any) => member.status === "interested").length,
          going: matchingMembers.filter((member: any) => member.status === "joined").length,
        };
      });

      if (!active) return;

      setAdventures(nextAdventures ?? []);
      setMemberCounts(nextMemberCounts);
      setEvents(
        orderEventsForMember(
          (eventRows ?? []) as any[],
          { postcode: profile?.postcode ?? null, countryCode: profile?.country_code ?? null },
          user?.id,
          userCoordinates,
        ) as any[],
      );
      setLinkedAdventureByEvent(nextLinkedAdventureByEvent);
      setPostcode(profile?.postcode ?? null);
      setCountryCode(profile?.country_code ?? null);
      setSaved(new Set((savedEvents ?? []).map((item: any) => item.event_id)));
      setLoading(false);
    };

    void load();

    return () => {
      active = false;
    };
  }, [user]);

  const enrichedAdventures = useMemo(
    () =>
      orderAdventuresByNextOccurrence(
        adventures.map((adventure: any) => ({
          ...adventure,
          organiser_name: adventure.profiles?.display_name ?? null,
          interested_count: memberCounts[adventure.id]?.interested ?? 0,
          going_count: memberCounts[adventure.id]?.going ?? 0,
        })),
      ),
    [adventures, memberCounts]
  );

  const groupedByType = useMemo(() => {
    const groups: Record<string, any[]> = {};
    enrichedAdventures.forEach((adventure: any) => {
      if (!groups[adventure.type]) groups[adventure.type] = [];
      groups[adventure.type].push(adventure);
    });
    return groups;
  }, [enrichedAdventures]);

  const filteredEvents = useMemo(() => {
    const viewFiltered = filterEventsForView(events, filter, SCHOOL_HOLIDAYS);
    const hasDistanceAwareEvents = viewFiltered.some((event) => event.distance_miles !== null && event.distance_miles !== undefined);
    if (!postcode || !hasDistanceAwareEvents) {
      return viewFiltered;
    }

    return filterEventsByDistance(viewFiltered, distanceLimitMiles);
  }, [distanceLimitMiles, events, filter, postcode]);
  const hasDistanceAwareEvents = useMemo(
    () => events.some((event) => event.distance_miles !== null && event.distance_miles !== undefined),
    [events],
  );

  const handleSectionChange = (nextValue: ActivitySection) => {
    setSearchParams(nextValue === "all" ? {} : { section: nextValue });
  };

  const toggleSave = async (eventId: string) => {
    if (!user) return;

    if (saved.has(eventId)) {
      await supabase.from("saved_events").delete().eq("event_id", eventId).eq("user_id", user.id);
      setSaved((current) => {
        const next = new Set(current);
        next.delete(eventId);
        return next;
      });
      return;
    }

    await supabase.from("saved_events").insert({ event_id: eventId, user_id: user.id });
    setSaved((current) => new Set(current).add(eventId));
    toast.success("Event saved");
  };

  const eventFilters: { key: EventFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "free", label: "Free" },
    { key: "low-cost", label: "Low-cost" },
    { key: "this-week", label: "This week" },
    { key: "school-holidays", label: "School holidays" },
  ];
  const distanceSummary =
    postcode && hasDistanceAwareEvents && distanceLimitMiles !== null
      ? `Soonest events first within ${distanceLimitMiles} miles of ${postcode}`
      : postcode && countryCode === "IE"
        ? `Soonest events first for ${postcode} and your local tags`
        : `Soonest events first for ${postcode || "you"}`;

  const buildAdventureSharePayload = (adventure: any) => ({
    version: 1 as const,
    type: "activity" as const,
    targetId: adventure.id,
    title: adventure.title,
    subtitle: `${formatDateRange(adventure.date_start, adventure.date_end)} · ${adventure.location}`,
    imageUrl: adventure.image_url ?? null,
    route: `/adventures/${adventure.id}`,
    externalUrl: buildKindredWebUrl(`/adventures/${adventure.id}`),
    accessScope: "public" as const,
  });

  const buildEventSharePayload = (event: any) => ({
    version: 1 as const,
    type: "event" as const,
    targetId: event.id,
    title: event.title,
    subtitle: `${format(new Date(event.event_date), "EEEE d MMMM yyyy, h:mma")} · ${event.location}`,
    imageUrl: event.image_url ?? null,
    route: `/whats-on/${event.id}`,
    externalUrl: buildKindredWebUrl(`/whats-on/${event.id}`),
    accessScope: "public" as const,
  });

  if (loading) {
    return <AppLoadingState title="Loading activities" body="Pulling together group plans and What’s On." />;
  }

  return (
    <AppSection pt="$xs" pb="$sm" gap="$sm">
      <AppCard>
        <AppCardBody gap="$sm">
          <AppSegmentedControl
            value={section}
            onChange={(value) => handleSectionChange(value as ActivitySection)}
            options={[
              { value: "all", label: "All" },
              { value: "by-type", label: "By Type" },
              { value: "whats-on", label: "What's On" },
            ]}
          />
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardBody gap="$sm">
          <AppButtonRow flexWrap="wrap">
            <AppButton type="button" tone="primary" size="sm" onPress={() => navigate("/adventures/new")}>
                <Plus size={16} />
                Set up activity
            </AppButton>
          </AppButtonRow>
        </AppCardBody>
      </AppCard>

      {activitiesError && section !== "whats-on" ? (
        <AppInlineAlert title="Activities are still refreshing" tone="warning">
          {activitiesError}
        </AppInlineAlert>
      ) : null}

      {section === "by-type" ? (
        !activitiesError && enrichedAdventures.length === 0 ? (
          <AppEmptyState title="No activities yet" body="Be the first to post one." />
        ) : (
          <AppSection gap="$md">
            {ADVENTURE_TYPES.map((typeConfig) => {
              const group = groupedByType[typeConfig.value];
              if (!group?.length) return null;

              return (
                <AppSection key={typeConfig.value} gap="$sm">
                  <AppSectionHeader title={`${typeConfig.emoji} ${typeConfig.label}`} />
                  <AppSection gap="$xs">
                    {group.map((adventure: any) => (
                      <AdventureCard
                        key={adventure.id}
                        adventure={adventure}
                        onClick={() => navigate(`/adventures/${adventure.id}`)}
                        action={<KindredShareSheet payload={buildAdventureSharePayload(adventure)} triggerContent={<Share2 size={16} />} iconOnly />}
                      />
                    ))}
                  </AppSection>
                </AppSection>
              );
            })}
          </AppSection>
        )
      ) : null}

      {section === "all" ? (
        !activitiesError && enrichedAdventures.length === 0 ? (
          <AppEmptyState title="No activities yet" body="Be the first to post one." />
        ) : (
          <AppSection gap="$sm">
            {enrichedAdventures.map((adventure: any) => (
              <AdventureCard
                key={adventure.id}
                adventure={adventure}
                onClick={() => navigate(`/adventures/${adventure.id}`)}
                action={<KindredShareSheet payload={buildAdventureSharePayload(adventure)} triggerContent={<Share2 size={16} />} iconOnly />}
              />
            ))}
          </AppSection>
        )
      ) : null}

      {section === "whats-on" ? (
        <AppSection gap="$sm">
          <AppCard>
            <AppCardBody gap="$sm">
              <AppButtonRow flexWrap="wrap">
                <AppButton type="button" tone="primary" size="sm" onPress={() => navigate("/whats-on/new")}>
                  <Plus size={16} />
                  Share local event
                </AppButton>
              </AppButtonRow>
            </AppCardBody>
          </AppCard>

          <AppCard>
            <AppCardBody flexDirection="row" alignItems="center" gap="$sm">
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                <span>📍 {distanceSummary}</span>
              </AppText>
            </AppCardBody>
          </AppCard>

          <AppButtonRow>
            {eventFilters.map((item) => (
              <AppButton
                key={item.key}
                type="button"
                size="sm"
                tone={filter === item.key ? "primary" : "outline"}
                onPress={() => setFilter(item.key)}
              >
                {item.label}
              </AppButton>
            ))}
          </AppButtonRow>

          {postcode && hasDistanceAwareEvents ? (
            <AppButtonRow flexWrap="wrap">
              {WHATS_ON_DISTANCE_OPTIONS.map((item) => (
                <AppButton
                  key={item.label}
                  type="button"
                  size="sm"
                  tone={distanceLimitMiles === item.value ? "primary" : "outline"}
                  onPress={() => setDistanceLimitMiles(item.value)}
                >
                  {item.label}
                </AppButton>
              ))}
            </AppButtonRow>
          ) : null}

          {whatsOnError ? (
            <AppInlineAlert title="What’s On is still refreshing" tone="warning">
              {whatsOnError}
            </AppInlineAlert>
          ) : null}

          {!whatsOnError && filteredEvents.length === 0 ? (
            <AppEmptyState
              title="No events found"
              body={
                distanceLimitMiles !== null && hasDistanceAwareEvents
                  ? "Try widening the distance or choosing another filter."
                  : "Try another filter or check back soon."
              }
            />
          ) : (
            <AppSection gap="$sm">
              {filteredEvents.map((event) => {
                const linkedAdventure = linkedAdventureByEvent[event.id];
                const openEvent = () => navigate(`/whats-on/${event.id}`);
                const sharePayload = buildEventSharePayload(event);

                return (
                  <AppCard key={event.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={openEvent}
                      onKeyDown={(keyboardEvent) => {
                        if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                          keyboardEvent.preventDefault();
                          openEvent();
                        }
                      }}
                      className="w-full cursor-pointer overflow-hidden text-left"
                    >
                      <AdventureHero
                        imageUrl={event.image_url}
                        storageBucket="event-photos"
                        badgeLabel="What's On"
                        className="flex h-[140px] items-end p-4"
                      />

                      <AppCardBody gap="$sm">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                              {event.title}
                            </AppText>
                          </div>

                          <div className="flex flex-shrink-0 items-center gap-1.5">
                            <div
                              onClick={(clickEvent) => clickEvent.stopPropagation()}
                              onKeyDown={(keyboardEvent) => keyboardEvent.stopPropagation()}
                            >
                              <KindredShareSheet
                                payload={sharePayload}
                                triggerContent={<Share2 size={16} />}
                                iconOnly
                              />
                            </div>
                            <button
                              onClick={(clickEvent) => {
                                clickEvent.stopPropagation();
                                void toggleSave(event.id);
                              }}
                              className="flex-shrink-0"
                            >
                              <Bookmark size={20} className={saved.has(event.id) ? "fill-primary text-primary" : "text-muted-foreground"} />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Calendar size={14} />
                          <span>{format(new Date(event.event_date), "EEE d MMM yyyy, h:mma")}</span>
                        </div>

                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <MapPin size={14} />
                          <span>{event.location}</span>
                        </div>

                        <div>
                          {event.relevance_score > 0 ? (
                            <span className="inline-block rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-medium text-secondary">
                              Near you
                            </span>
                          ) : null}
                          {event.is_free ? (
                            <span className={`${event.relevance_score > 0 ? "ml-2 " : ""}inline-block rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800`}>
                              Free
                            </span>
                          ) : event.cost ? (
                            <span className={`${event.relevance_score > 0 ? "ml-2 " : ""}inline-block rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground`}>
                              {event.cost}
                            </span>
                          ) : null}

                          {linkedAdventure ? (
                            <span className="ml-2 inline-block rounded-full bg-secondary/10 px-2.5 py-0.5 text-xs font-medium text-secondary">
                              Group plan · {linkedAdventure.going} going
                            </span>
                          ) : null}
                        </div>
                      </AppCardBody>
                    </div>
                  </AppCard>
                );
              })}
            </AppSection>
          )}
        </AppSection>
      ) : null}
    </AppSection>
  );
};

export default Activities;
